import { readCookieEverywhere } from '~/utils/cookie';

/**
 * First-party record that this browser has opted out of the sale or sharing of
 * personal information.
 *
 * Deliberately a separate flag from the Global Privacy Control signal rather
 * than folded into it. GPC is a browser hint read per request from the `Sec-GPC`
 * header; this is an explicit choice a person made on our own page. Support and
 * the DSR runbook need to tell the two apart, so they stay distinguishable all
 * the way through and are only combined at the point of deciding whether to
 * suppress third-party sharing.
 */
export const SALE_SHARING_OPT_OUT_COOKIE = '__ap_sale_sharing_opt_out';

/**
 * The opt-out was stored against the email address as well as this browser.
 */
export const SALE_SHARING_OPT_OUT_RECORDED = '1';

/**
 * The browser is opted out but the backend write did not land, so the person
 * still has to submit again to extend the opt-out to their account.
 *
 * This exists so the cookie cannot be mistaken for proof the whole opt-out
 * succeeded. Both values suppress sharing for the browser; only
 * {@link SALE_SHARING_OPT_OUT_RECORDED} means the request was captured.
 */
export const SALE_SHARING_OPT_OUT_BROWSER_ONLY = 'browser';

/**
 * 400 days in seconds — the longest expiry Chrome will honour, so anything
 * larger is silently clamped. An opt-out is meant to persist, so this is set as
 * far out as browsers allow and refreshed on every submission.
 */
export const SALE_SHARING_OPT_OUT_MAX_AGE_SECONDS = 60 * 60 * 24 * 400;

const readValue = (cookieHeader?: string | null): string | undefined =>
    readCookieEverywhere(SALE_SHARING_OPT_OUT_COOKIE, cookieHeader);

/**
 * Whether this browser carries an opt-out, recorded or not. This is the
 * suppression signal: a person who submitted the form has exercised their
 * choice for this browser whether or not the backend write landed.
 *
 * Reads the passed `Cookie:` header during SSR and falls back to
 * `document.cookie` in the browser, so the same helper serves the server
 * context and the client-side pixel check.
 */
export const hasSaleSharingOptOutCookie = (
    cookieHeader?: string | null,
): boolean => {
    const value = readValue(cookieHeader);
    return (
        value === SALE_SHARING_OPT_OUT_RECORDED ||
        value === SALE_SHARING_OPT_OUT_BROWSER_ONLY
    );
};

/**
 * Whether the opt-out still needs to be submitted again to reach the account.
 *
 * The browser is already covered; what is outstanding is the email-keyed
 * record. Kept distinct from {@link hasSaleSharingOptOutCookie} so the page can
 * suppress sharing and still ask the person to finish, rather than telling them
 * their opt-out was recorded when it was not.
 */
export const isSaleSharingOptOutPending = (
    cookieHeader?: string | null,
): boolean => readValue(cookieHeader) === SALE_SHARING_OPT_OUT_BROWSER_ONLY;

/**
 * Builds the `Set-Cookie` value the opt-out endpoint returns.
 *
 * Written server-side rather than via `document.cookie` so the flag is already
 * present on the next document request and SSR can suppress pixels before any
 * of them render. Not `HttpOnly`: the client reads it at hydration, mirroring
 * how the GPC signal is read from `navigator`.
 *
 * `topDomain` scopes the cookie across subdomains when configured. It is absent
 * on localhost and preview hosts, where the browser would reject a `domain=`
 * that does not match the origin, so we fall back to a host-scoped cookie.
 */
export const buildSaleSharingOptOutCookie = (
    topDomain: string | undefined,
    recorded: boolean,
): string => {
    const value = recorded
        ? SALE_SHARING_OPT_OUT_RECORDED
        : SALE_SHARING_OPT_OUT_BROWSER_ONLY;
    const base = `${SALE_SHARING_OPT_OUT_COOKIE}=${value}; path=/; max-age=${SALE_SHARING_OPT_OUT_MAX_AGE_SECONDS}; SameSite=Lax; Secure`;
    return topDomain ? `${base}; domain=${topDomain}` : base;
};
